import { useReadContract } from 'wagmi';
import { ERC20_ABI } from './abis';
import { getTokenAddress } from './contracts';
import { TARGET_CHAIN } from './config';

// Global cache for token decimals by token address (or symbol for native)
const decimalsCache = new Map<string, number>();

// Native BNB always uses 18 decimals
decimalsCache.set('BNB', 18);

export function useTokenDecimals(symbol: string): number {
  const readChainId = TARGET_CHAIN.id;
  const isNative = symbol === 'BNB';
  const tokenAddress = getTokenAddress(symbol, readChainId);
  const cacheKey = isNative ? 'BNB' : tokenAddress?.toLowerCase();

  const cachedValue = cacheKey ? decimalsCache.get(cacheKey) : undefined;

  const { data: decimalsData } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'decimals',
    chainId: readChainId,
    query: {
      enabled: Boolean(tokenAddress && !isNative && cachedValue === undefined),
      staleTime: Infinity,
    },
  });

  if (cachedValue !== undefined) {
    return cachedValue;
  }

  if (decimalsData !== undefined) {
    const dec = Number(decimalsData);
    if (cacheKey) {
      decimalsCache.set(cacheKey, dec);
    }
    return dec;
  }

  return 18; // Default fallback
}

export function getCachedDecimals(symbol: string): number {
  if (symbol === 'BNB') return 18;
  const tokenAddress = getTokenAddress(symbol, TARGET_CHAIN.id);
  const cacheKey = tokenAddress?.toLowerCase();
  return (cacheKey ? decimalsCache.get(cacheKey) : undefined) ?? 18;
}
